import { useRef, useState, type ReactNode } from 'react';
import {
  ORG_ROLES,
  invitationCreateSchema,
  type InvitationSummary,
  type OrgRole,
  type ProductKey,
} from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { CheckboxGroup, ReasonField, TextField, reasonError } from '../../components/fields';
import { EMPLOYEE_PRODUCTS, ProductGrantsField, ROLE_DESCRIPTIONS } from '../../components/grants';
import { DateText, ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { ORG_ROLE_LABELS, formatDate, productShortName } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { useOrganizationScope, useSupportAccessHint } from './scope';

const DEFAULT_EXPIRY_DAYS = 7;
const MAX_EXPIRY_DAYS = 30;

/** A link to render, only when it is an http or https URL; anything else stays text. */
export function httpUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * The link the development delivery recorded for an invitation. platform-api sends it only in development;
 * Trace Admin shows it only when it is built for development too, so a hosted build never offers one.
 */
function DevelopmentLink({
  invitation,
  environment,
}: {
  invitation: InvitationSummary;
  environment: string;
}) {
  if (environment !== 'development' || !invitation.developmentLink) return null;
  const href = httpUrl(invitation.developmentLink);
  return href ? (
    <a
      className="admin-link"
      href={href}
      rel="noreferrer"
      aria-label={`Open the invitation for ${invitation.emailHint}`}
    >
      Open the invitation
    </a>
  ) : (
    <code>{invitation.developmentLink}</code>
  );
}

interface InviteForm {
  email: string;
  roles: OrgRole[];
  grants: Partial<Record<ProductKey, string>>;
  expiresInDays: string;
}

type InviteErrors = Partial<Record<'email' | 'roles' | 'expiresInDays', string>>;

function validateInvite(form: InviteForm): InviteErrors {
  const errors: InviteErrors = {};
  const email = form.email.trim();
  if (!email) errors.email = 'Enter an email address.';
  else if (!invitationCreateSchema.shape.email.safeParse(email).success)
    errors.email = 'Enter a valid email address.';
  if (form.roles.length === 0) errors.roles = 'Choose at least one role.';
  const days = Number(form.expiresInDays);
  if (!Number.isInteger(days) || days < 1 || days > MAX_EXPIRY_DAYS) {
    errors.expiresInDays = `Enter a whole number of days from 1 to ${MAX_EXPIRY_DAYS}.`;
  }
  return errors;
}

type InvitationDialog = { kind: 'create' } | { kind: 'revoke'; invitation: InvitationSummary };

/**
 * Invitations (/organizations/:id/invitations): every invitation with its roles, products, status and
 * dates. Staff who may manage members invite someone (email, roles, products, days until it expires) and
 * revoke a pending invitation with a reason. In development the link the local delivery recorded is shown
 * to open it directly; hosted environments never have one.
 */
export function InvitationsTab() {
  const { organization, abilities } = useOrganizationScope();
  const hint = useSupportAccessHint();
  const { api, environment } = useAdmin();
  const organizationId = organization.id;
  const key = `invitations\u0000${organizationId}`;
  const read = useAdminRead(key, (signal) => api.listInvitations(organizationId, signal));
  const invitations = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;
  const showLinks = environment === 'development';

  const [notice, setNotice] = useState<string | null>(null);
  const [created, setCreated] = useState<InvitationSummary | null>(null);
  const dialog = useDialogTarget<InvitationDialog>();
  const [form, setForm] = useState<InviteForm>({
    email: '',
    roles: ['member'],
    grants: {},
    expiresInDays: '',
  });
  const [errors, setErrors] = useState<InviteErrors>({});
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  // The invitation the last submit created, for the notice and the development link.
  const lastCreated = useRef<InvitationSummary | null>(null);

  const candidates = EMPLOYEE_PRODUCTS.filter((product) =>
    organization.products.some((p) => p.productKey === product),
  );

  const open = (next: InvitationDialog) => {
    setErrors({});
    setReason('');
    setReasonProblem(null);
    if (next.kind === 'create') {
      setForm({ email: '', roles: ['member'], grants: {}, expiresInDays: String(DEFAULT_EXPIRY_DAYS) });
    }
    dialog.show(next);
  };

  const target = dialog.target;
  let dialogView: ReactNode = null;
  if (target?.kind === 'create') {
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Invite someone to ${organization.displayName}`}
        description="They get a link to accept it with their own sign-in."
        submitLabel="Send the invitation"
        onSubmit={async () => {
          const found = validateInvite(form);
          setErrors(found);
          if (Object.keys(found).length > 0) return 'invalid';
          lastCreated.current = null;
          lastCreated.current = await api.createInvitation(organizationId, {
            email: form.email.trim(),
            roles: form.roles,
            products: candidates
              .filter((product) => form.grants[product] !== undefined)
              .map((product) => ({ productKey: product, productRole: form.grants[product]?.trim() || null })),
            expiresInDays: Number(form.expiresInDays),
          });
          return undefined;
        }}
        onSaved={() => {
          const invitation = lastCreated.current;
          setCreated(invitation);
          setNotice(
            invitation
              ? `Invitation sent to ${invitation.emailHint}. It expires on ${formatDate(invitation.expiresAt)}.`
              : 'Invitation sent.',
          );
          read.retry();
        }}
      >
        <TextField
          label="Email address"
          type="email"
          value={form.email}
          onChange={(value) => setForm((current) => ({ ...current, email: value }))}
          error={errors.email ?? null}
          maxLength={320}
          autoComplete="off"
          spellCheck={false}
          required
        />
        <CheckboxGroup
          legend="Organization roles"
          choices={ORG_ROLES.map((role) => ({
            value: role,
            label: ORG_ROLE_LABELS[role],
            description: ROLE_DESCRIPTIONS[role],
          }))}
          values={form.roles}
          onChange={(values) => setForm((current) => ({ ...current, roles: values as OrgRole[] }))}
          error={errors.roles ?? null}
        />
        {candidates.length > 0 ? (
          <ProductGrantsField
            candidates={candidates}
            grants={form.grants}
            onChange={(grants) => setForm((current) => ({ ...current, grants }))}
          />
        ) : (
          <p className="admin-muted">The organization has no product to grant yet.</p>
        )}
        <TextField
          label="Days until it expires"
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_EXPIRY_DAYS}
          value={form.expiresInDays}
          onChange={(value) => setForm((current) => ({ ...current, expiresInDays: value }))}
          hint={`From 1 to ${MAX_EXPIRY_DAYS} days.`}
          error={errors.expiresInDays ?? null}
          required
        />
      </WriteDialog>
    );
  } else if (target?.kind === 'revoke') {
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Revoke the invitation for ${target.invitation.emailHint}?`}
        submitLabel="Revoke the invitation"
        tone="danger"
        onSubmit={async () => {
          const problem = reasonError(reason);
          setReasonProblem(problem);
          if (problem) return 'invalid';
          await api.revokeInvitation(organizationId, target.invitation.id, reason);
          return undefined;
        }}
        onSaved={() => {
          setNotice(`The invitation for ${target.invitation.emailHint} is revoked.`);
          read.retry();
        }}
      >
        <p>Its link stops working at once. You can invite the same person again later.</p>
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  }

  return (
    <>
      <SavedNotice message={notice} />
      {showLinks && created ? (
        <div className="admin-dev-note" role="note" aria-label="Development link">
          <p>
            <strong>Development only.</strong> The local delivery recorded this link for {created.emailHint}:{' '}
            <DevelopmentLink invitation={created} environment={environment} />
          </p>
        </div>
      ) : null}
      <PageSection
        title="Invitations"
        description="Invitations to join this organization. A person joins by accepting theirs with their own sign-in."
        action={
          abilities.manageMembers ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => open({ kind: 'create' })}
            >
              Invite someone
            </button>
          ) : undefined
        }
      >
        {read.failure ? (
          <AdminProblem
            failure={read.failure}
            subject="the invitations"
            onRetry={read.retry}
            supportAccess={hint}
          />
        ) : !invitations ? (
          <LoadingBlock label="Loading the invitations" />
        ) : invitations.length === 0 ? (
          <EmptyPageState
            icon={<NavigationGlyph name="Users" />}
            title="No invitations yet"
            description="Invitations appear here once someone is invited to this organization."
          />
        ) : (
          <ScrollTable
            caption="Invitations"
            busy={read.loading}
            className="admin-table admin-table--invitations"
          >
            <thead>
              <tr>
                <th scope="col">Invited</th>
                <th scope="col">Roles</th>
                <th scope="col">Products</th>
                <th scope="col">Status</th>
                <th scope="col">Sent</th>
                <th scope="col">Expires</th>
                {showLinks ? <th scope="col">Development link</th> : null}
                {abilities.manageMembers ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {invitations.map((invitation) => (
                <tr key={invitation.id}>
                  <th scope="row">{invitation.emailHint}</th>
                  <td>
                    {invitation.roles && invitation.roles.length > 0 ? (
                      invitation.roles.map((role) => ORG_ROLE_LABELS[role]).join(', ')
                    ) : (
                      <span className="admin-muted">Not given</span>
                    )}
                  </td>
                  <td>
                    {invitation.productKeys && invitation.productKeys.length > 0 ? (
                      invitation.productKeys.map((product) => productShortName(product)).join(', ')
                    ) : (
                      <span className="admin-muted">None</span>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={invitation.status} />
                  </td>
                  <td>
                    <DateText iso={invitation.createdAt} />
                  </td>
                  <td>
                    <DateText iso={invitation.expiresAt} />
                  </td>
                  {showLinks ? (
                    <td>
                      {invitation.status === 'pending' && invitation.developmentLink ? (
                        <DevelopmentLink invitation={invitation} environment={environment} />
                      ) : (
                        <span className="admin-muted">None</span>
                      )}
                    </td>
                  ) : null}
                  {abilities.manageMembers ? (
                    <td>
                      {invitation.status === 'pending' ? (
                        <button
                          type="button"
                          className="admin-action admin-action--danger"
                          aria-label={`Revoke the invitation for ${invitation.emailHint}`}
                          onClick={() => open({ kind: 'revoke', invitation })}
                        >
                          Revoke
                        </button>
                      ) : (
                        <span className="admin-muted">None</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        )}
      </PageSection>
      {dialogView}
    </>
  );
}
